import {Eye} from "lucide-react";

import {Button} from "@/components/ui/button";
import Table from "@/components/ui/table";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {TruncatedWithTooltip} from "@/src/modules/core/components";
import {cn} from "@/lib/utils";

import type {VehicleCheck, VehicleCheckMatch} from "../types";
import {VehicleCheckAuthorizationStatus} from "./vehicle-check-authorization-status";
import {VehicleCheckMatchStatus} from "./vehicle-check-match-status";

type VehicleCheckMatchCardProps = {
  match: VehicleCheckMatch;
  vehicleCheck?: VehicleCheck | null;
  isStatusPending: boolean;
  isChecked: boolean;
  onCheck: () => void;
  onView: () => void;
  className?: string;
};

export function VehicleCheckMatchCard({
  match,
  vehicleCheck,
  isStatusPending,
  isChecked,
  onCheck,
  onView,
  className,
}: VehicleCheckMatchCardProps) {
  const vehicle = match.vehicle;
  const specLine = [vehicle.make, vehicle.model, vehicle.color].filter(Boolean).join(" · ");

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-xs",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <TruncatedWithTooltip content={vehicle.plate || "—"}>
            <p className="text-base font-semibold leading-tight">{vehicle.plate || "—"}</p>
          </TruncatedWithTooltip>
          {specLine ? (
            <p className="text-muted-foreground mt-0.5 text-xs leading-snug">{specLine}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant={isChecked ? "secondary" : "default"}
            className="h-8 px-2.5"
            disabled={isStatusPending || isChecked}
            onClick={onCheck}
          >
            {isChecked ? "Checked" : "Check"}
          </Button>
          <Tooltip>
            <TooltipTrigger
              render={
                <Table.ActionButton
                  action="details"
                  variant="details"
                  icon={Eye}
                  title="View vehicle"
                  disabled={isStatusPending}
                  onClick={onView}
                />
              }
            />
            <TooltipContent>View vehicle</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-3">
        <VehicleCheckAuthorizationStatus
          check={vehicleCheck}
          vehicleId={vehicle.vehicle_id}
          showPlaceholders={Boolean(vehicleCheck)}
        />
        <VehicleCheckMatchStatus vehicle={vehicle} />
      </div>
    </div>
  );
}
